import { Select, Spin, Switch } from 'antd'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { getBookingProviderTags } from '../../../redux/bookingsSlice';
import SaveButton from '../../UI/savebutton';
import { updateOrganization } from '../../../redux/organizationSlice';

const ServiceTitanIntegration = () => {
    const {organizationData} = useSelector(
        (state) => state?.organization
    );
    const [isServiceTitanEnabled, setIsServiceTitanEnabled] = useState(organizationData?.bookingsConfig?.serviceTitan_enabled || false)
    const [selectedTag, setSelectedTag] = useState({
        id: organizationData?.bookingsConfig?.serviceTitan_bookingProviderTags_id || "",
        name: organizationData?.bookingsConfig?.serviceTitan_bookingProviderTags_name || ""
    })
    const [availableTags, setAvailableTags] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [isDirty, setIsDirty] = useState(false)

    const {currentOrganizationId} = useSelector(
        (state) => state?.auth
    );

    const dispatch = useDispatch()
    
    useEffect(() => {
        const fetchBookingProviderTags = async () => {
            try {
                setLoading(true)
                dispatch(getBookingProviderTags({currentOrganizationId})).unwrap().then((res) => {
                    setAvailableTags(res.data.map(tag => ({
                        label: tag.tagName,
                        value: tag.id,
                        description: tag.description
                    })))
                })
                setLoading(false)
            } catch (error) {
                console.error('Error fetching booking tags:', error)
                setLoading(false)
            }
        }
        fetchBookingProviderTags()
    }, [currentOrganizationId, dispatch])

    useEffect(() => {
        setIsServiceTitanEnabled(organizationData?.bookingsConfig?.serviceTitan_enabled || false)
        setSelectedTag({
            id: organizationData?.bookingsConfig?.serviceTitan_bookingProviderTags_id || "",
            name: organizationData?.bookingsConfig?.serviceTitan_bookingProviderTags_name || ""
        })
    }, [organizationData])

    const handleServiceTitanChange = (checked) => {
        setIsServiceTitanEnabled(checked)
        setIsDirty(true)
        if (!checked) {
            setSelectedTag({ id: "", name: "" })
            setError('')
        }
    }

    const handleTagChange = (value, option) => {
        setSelectedTag({
            id: value,
            name: option?.label || ""
        })
        setIsDirty(true)
        setError(!value ? 'Please select a tag' : '')
    }

    const handleSave = async () => {
        try {
            setLoading(true)
            await dispatch(updateOrganization({
                id: organizationData._id,
                payload: {
                    bookingsConfig: {
                        serviceTitan_enabled: isServiceTitanEnabled,
                        serviceTitan_bookingProviderTags_id: selectedTag.id,
                        serviceTitan_bookingProviderTags_name: selectedTag.name
                    }
                },
                page: "Service Titan Integration"
            })).unwrap()
            setIsDirty(false)
        } catch (error) {
            console.error('Error updating organization:', error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="max-w-4xl mx-auto my-10">
            <Spin spinning={loading} fullscreen />
            <div className="bg-white shadow-lg rounded-xl overflow-hidden">
                <div className="flex items-center gap-4 bg-black p-6 w-full text-white">
                    <img
                        src={require("../../../assets/Images/service-titan.png")}
                        alt="Service Titan Logo"
                        className="w-10 h-10 object-contain"
                    />
                    <h2 className="text-2xl font-semibold text-white m-0">
                        ServiceTitan Integration
                    </h2>
                </div>

                <div className="p-8 space-y-6">
                    <div className="flex items-center justify-between p-6 bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200">
                        <div className="flex-1">
                            <label className="font-medium text-gray-800 text-lg">
                                Record Follow-up Leads in ServiceTitan
                            </label>
                            <p className="text-gray-600 mt-2 text-sm">
                                Enable this to automatically record your follow-up leads as <strong>Bookings</strong> in ServiceTitan
                            </p>
                        </div>
                        <Switch
                            checked={isServiceTitanEnabled}
                            onChange={handleServiceTitanChange}
                            className="scale-125"
                        />
                    </div>

                    {isServiceTitanEnabled && (
                        <div className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-200 transition-all duration-300 ease-in-out">
                            <div className="flex items-center gap-2 mb-2">
                                <label className="block font-medium text-gray-800 text-lg">
                                    Booking Provider Tag
                                </label>
                                <span className="text-red-500 text-sm font-medium">*Required</span>
                            </div>
                            <p className="text-gray-600 mb-4 text-sm">
                                Select a tag that will be applied to bookings in ServiceTitan
                            </p>
                            <Select
                                loading={loading}
                                placeholder="Select a booking provider tag"
                                options={availableTags}
                                value={selectedTag.name || null}
                                onChange={handleTagChange}
                                className={`w-full ${error ? 'border-red-300' : ''}`}
                                status={error ? 'error' : ''}
                                optionRender={(option) => (
                                    <div>
                                        <div className="font-medium">{option.data.label}</div>
                                        {option.data.description && (
                                            <div className="text-xs text-gray-500">{option.data.description}</div>
                                        )}
                                    </div>
                                )}
                                style={{ 
                                    minHeight: '40px',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                                }}
                            />
                            {error && (
                                <p className="mt-2 text-red-500 text-sm">
                                    {error}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <SaveButton 
                isDirty={isDirty} 
                onSubmit={handleSave}
                loading={loading}
                disabled={!isDirty}
            />
        </div>
    )
}

export default ServiceTitanIntegration